import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import parse from 'html-react-parser';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { setCalendarPerformance } from '../redux/reducer';
import API from '../services/API';
import { chartTooltipStyle, formatChartDate, formatChartTooltip } from './chartTooltip';

const CHART_TEXT_COLOR = '#050139';
const NEGATIVE_TEXT_COLOR = '#dc3545';
const SERIES_COLORS = ['#050139', '#2DCCD3'];

const getFullYear = value => {
  const date = new Date(value);
  return date.getFullYear();
};

const getChartTitle = () => window.performanceChart?.title || '';

const formatToTwoDecimalPlaces = num => (num == null ? null : (num.toFixed(4) * 100).toFixed(2));

const getOptions = data => {
  const idxBmkName = data.find(d => d.idxBmkName)?.idxBmkName;
  const productName = data.find(d => d.productName)?.productName;
  const series = [
    { name: productName, data: data.map(x => x.product) },
    { name: idxBmkName, data: data.map(x => x.idxBmk) },
  ]
    .filter(item => item.name && item.data.some(value => value !== null))
    .map((item, index) => ({
      ...item,
      type: 'bar',
      symbol: 'circle',
      barMaxWidth: 30,
      color: SERIES_COLORS[index],
    }));

  return {
    textStyle: { fontFamily: getComputedStyle(document.body).fontFamily },
    grid: { top: 30, right: 8, bottom: 85, left: 21, containLabel: true },
    tooltip: {
      ...chartTooltipStyle,
      trigger: 'axis',
      formatter: params => formatChartTooltip(params, value => `${value}%`),
      axisPointer: {
        type: 'cross',
        label: {
          formatter: params => {
            if (params.axisDimension === 'y') {
              return `${params.value.toFixed(2)}%`;
            }
            return formatChartDate(params.value);
          },
        },
      },
      valueFormatter: value => `${value}%`,
      textStyle: {
        fontSize: 16,
        color(value) {
          if (value < 0) {
            return NEGATIVE_TEXT_COLOR;
          }
          return CHART_TEXT_COLOR;
        },
      },
    },
    legend: {
      show: true,
      data: series.map(item => item.name),
      top: 'bottom',
      textStyle: {
        color: CHART_TEXT_COLOR,
        fontSize: 16,
      },
    },
    xAxis: [
      {
        type: 'category',
        data: data.map(x => x.dt),
        axisLabel: {
          rotate: 45,
          formatter: getFullYear,
          color: CHART_TEXT_COLOR,
          fontSize: 16,
        },
        boundaryGap: true,
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: i18next.t('PERCENTAGE'),
        nameLocation: 'middle',
        nameGap: 55,
        nameTextStyle: { color: CHART_TEXT_COLOR, fontSize: 16 },
        axisLabel: {
          formatter(value) {
            return `${value}.0%`;
          },
          color: value => (value < 0 ? NEGATIVE_TEXT_COLOR : CHART_TEXT_COLOR),
          fontSize: 16,
        },
      },
    ],
    series,
    media: [
      {
        query: { maxWidth: 480 },
        option: {
          tooltip: { textStyle: { fontSize: 14 } },
          legend: {
            left: 8,
            orient: 'vertical',
            itemWidth: 12,
            itemHeight: 8,
            textStyle: { fontSize: 14, width: 210, overflow: 'truncate' },
          },
          xAxis: [{ axisLabel: { fontSize: 14 } }],
          yAxis: [
            {
              nameTextStyle: { fontSize: 14 },
              axisLabel: { fontSize: 14 },
            },
          ],
        },
      },
    ],
  };
};

const CalendarPerformanceChart = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let isActive = true;

    const fetchOptions = async () => {
      try {
        const isin = window.performanceChart?.isin;
        if (!isin || !isActive) return;

        const response = await API.getCalendarPerformanceChartData(isin);
        if (!Array.isArray(response) || response.length === 0 || !isActive) return;

        const updatedData = response.map(d => ({
          ...d,
          product: formatToTwoDecimalPlaces(d.product),
          idxBmk: formatToTwoDecimalPlaces(d.idxBmk),
        }));

        setOptions(getOptions(updatedData));
        dispatch(setCalendarPerformance(updatedData));
      } catch {
        if (isActive) setOptions(null);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchOptions();

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  return (
    <div>
      {loading && (
        <div className="loader-overlay">
          <div className="loader" />
        </div>
      )}
      {options && (
        <>
          <h3 className="typ-h3">{parse(getChartTitle())}</h3>
          <ReactECharts
            option={options}
            notMerge
            lazyUpdate
            onChartReady={() => {}}
            onEvents={() => {}}
            style={{ width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </>
      )}
    </div>
  );
};

export default CalendarPerformanceChart;
